/**
 * Shared data/CRUD layer for the "named entity + members" admin panels
 * (Teams, Organizations/Tenants): list, create, update, delete an entity,
 * and add/remove members from it. Extracted because TeamsManagementPanel
 * and OrganizationsManagementPanel otherwise hand-roll the exact same
 * fetch + error-handling shape for every one of these operations.
 *
 * The Modal/Drawer/Table JSX stays in each panel — only the fields and the
 * member-row shape differ meaningfully there, so forcing a single generic
 * component for that part would be harder to read than it's worth.
 */
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useUser } from '../contexts/UserContext';
import { buildAuthHeaders } from '../utils/userStore';

interface ApiError {
  detail?: string;
}

interface CrudWithMembersConfig<TSummary extends { members_count: number }> {
  /** Full base URL for the entity collection, e.g. `${EXECUTOR_API}/agent/builder/teams`. */
  basePath: string;
  /** Key holding the array in the list response, e.g. `{ teams: [...] }` → "teams". */
  listField: string;
  /** Entity id/slug used to build item and member sub-resource URLs. */
  getKey: (item: TSummary) => string;
  errorMessages?: {
    list?: string;
    create?: string;
    update?: string;
    delete?: string;
    addMember?: string;
  };
}

async function parseError(res: Response): Promise<string | undefined> {
  const err = (await res.json().catch(() => ({}))) as ApiError;
  return err.detail;
}

export function useCrudWithMembers<TSummary extends { members_count: number }, TDetail>(
  config: CrudWithMembersConfig<TSummary>
) {
  const { email: currentEmail } = useUser();
  const [items, setItems] = useState<TSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!currentEmail) return;
    setLoading(true);
    try {
      const res = await fetch(config.basePath, {
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        const data = (await res.json()) as Record<string, TSummary[]>;
        setItems(data[config.listField] || []);
      } else {
        message.error(config.errorMessages?.list || 'Error al cargar');
      }
    } catch {
      message.error('Error de red');
    } finally {
      setLoading(false);
    }
    // config is a fresh object literal on every render in the caller; its
    // contents (basePath/listField/getKey) are what actually matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmail, config.basePath, config.listField]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const create = useCallback(
    async (body: Record<string, unknown>): Promise<TSummary | null> => {
      try {
        const res = await fetch(config.basePath, {
          method: 'POST',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const created = (await res.json()) as TSummary;
          setItems((prev) => [...prev, created]);
          return created;
        }
        message.error((await parseError(res)) || config.errorMessages?.create || 'Error al crear');
        return null;
      } catch {
        message.error('Error de red');
        return null;
      }
    },
    [config.basePath, config.errorMessages?.create]
  );

  const fetchDetail = useCallback(
    async (key: string): Promise<TDetail | null> => {
      try {
        const res = await fetch(`${config.basePath}/${key}`, {
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        });
        if (res.ok) return (await res.json()) as TDetail;
      } catch {
        // Caller owns its own "couldn't load detail" message/loading state.
      }
      return null;
    },
    [config.basePath]
  );

  const update = useCallback(
    async (key: string, body: Record<string, unknown>): Promise<boolean> => {
      try {
        const res = await fetch(`${config.basePath}/${key}`, {
          method: 'PATCH',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        });
        if (res.ok) {
          setItems((prev) =>
            prev.map((it) => (config.getKey(it) === key ? { ...it, ...body } : it))
          );
          return true;
        }
        message.error(config.errorMessages?.update || 'Error al actualizar');
        return false;
      } catch {
        message.error('Error de red');
        return false;
      }
    },
    [config]
  );

  const remove = useCallback(
    async (item: TSummary): Promise<boolean> => {
      const key = config.getKey(item);
      try {
        const res = await fetch(`${config.basePath}/${key}`, {
          method: 'DELETE',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        });
        if (res.ok) {
          setItems((prev) => prev.filter((it) => config.getKey(it) !== key));
          return true;
        }
        message.error((await parseError(res)) || config.errorMessages?.delete || 'Error al eliminar');
        return false;
      } catch {
        message.error('Error de red');
        return false;
      }
    },
    [config]
  );

  const addMember = useCallback(
    async (key: string, body: Record<string, unknown>): Promise<boolean> => {
      try {
        const res = await fetch(`${config.basePath}/${key}/members`, {
          method: 'POST',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        });
        if (res.ok) {
          setItems((prev) =>
            prev.map((it) =>
              config.getKey(it) === key ? { ...it, members_count: it.members_count + 1 } : it
            )
          );
          return true;
        }
        message.error((await parseError(res)) || config.errorMessages?.addMember || 'No se pudo añadir el miembro');
        return false;
      } catch {
        message.error('Error de red');
        return false;
      }
    },
    [config]
  );

  const removeMember = useCallback(
    async (key: string, memberKey: string): Promise<boolean> => {
      try {
        const res = await fetch(`${config.basePath}/${key}/members/${encodeURIComponent(memberKey)}`, {
          method: 'DELETE',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        });
        if (res.ok) {
          setItems((prev) =>
            prev.map((it) =>
              config.getKey(it) === key
                ? { ...it, members_count: Math.max(0, it.members_count - 1) }
                : it
            )
          );
          return true;
        }
        message.error((await parseError(res)) || 'No se pudo quitar el miembro');
        return false;
      } catch {
        message.error('Error de red');
        return false;
      }
    },
    [config]
  );

  return { items, loading, fetchAll, create, fetchDetail, update, remove, addMember, removeMember };
}
